import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  signal,
} from '@angular/core';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { EmptyState } from '@shared/components/empty-state/empty-state';
import { StatusPill } from '@shared/components/status-pill/status-pill';
import { Subscription } from 'rxjs';
import { TableCellTemplateDirective } from './table-cell-template.directive';

export type TableColumnAlign = 'left' | 'center' | 'right';

export interface TableColumn {
  key: string;
  label: string;
  align?: TableColumnAlign;
  wrap?: boolean;
}

@Component({
  selector: 'app-table',
  imports: [NgClass, NgTemplateOutlet, MatMenuModule, EmptyState, StatusPill],
  templateUrl: './table.html',
  styleUrl: './table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Table implements AfterContentInit, OnDestroy {
  @Input() isLoading = signal<boolean>(false);
  @Input() emptyStateMessage: string = 'No data available.';

  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() showOptions: boolean = false;
  @Input() menuTemplate?: MatMenu;
  @Output() rowClick = new EventEmitter<any>();
  @Output() moreOptions = new EventEmitter<any>();
  @Output() menuOpened = new EventEmitter<any>();

  @ContentChildren(TableCellTemplateDirective)
  private cellTemplateDirectives!: QueryList<TableCellTemplateDirective>;

  private readonly cellTemplateMap = signal<
    Record<string, TemplateRef<unknown>>
  >({});

  private cellTemplatesSub?: Subscription;

  ngAfterContentInit(): void {
    this.refreshCellTemplateMap();
    this.cellTemplatesSub = this.cellTemplateDirectives.changes.subscribe(() =>
      this.refreshCellTemplateMap(),
    );
  }

  ngOnDestroy(): void {
    this.cellTemplatesSub?.unsubscribe();
  }

  cellTemplateFor(columnKey: string): TemplateRef<unknown> | null {
    return this.cellTemplateMap()[columnKey] ?? null;
  }

  cellOutletContext(row: unknown): { $implicit: unknown; row: unknown } {
    return { $implicit: row, row };
  }

  onRowClick(row: any) {
    this.rowClick.emit(row);
  }
  onMoreOptions(row: any) {
    this.moreOptions.emit(row);
  }

  onMenuOpen(row: any) {
    this.menuOpened.emit(row);
  }

  headerCellClass(column: TableColumn): string {
    return `align-middle px-2 py-3 sm:py-4 text-xs font-medium bg-[#F7F7F7] text-[#636363] uppercase tracking-wider whitespace-nowrap ${this.alignClass(column)}`;
  }

  bodyCellClass(column: TableColumn): string {
    const wrapClass = column.wrap ? 'whitespace-normal' : 'whitespace-nowrap';

    return `align-middle px-2 py-3 sm:py-4 text-sm text-[#636363] ${wrapClass} ${this.alignClass(column)}`;
  }

  private alignClass(column: TableColumn): string {
    const align = column.align ?? 'center';

    switch (align) {
      case 'left':
        return 'text-left';
      case 'right':
        return 'text-right';
      case 'center':
        return 'text-center';
      default: {
        const _exhaustive: never = align;
        return _exhaustive;
      }
    }
  }

  private refreshCellTemplateMap(): void {
    const map: Record<string, TemplateRef<unknown>> = {};
    for (const directive of this.cellTemplateDirectives) {
      map[directive.columnKey] = directive.template;
    }
    this.cellTemplateMap.set(map);
  }
}
